<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  media: any
  categories: any,
  activeCategory: any
}>()

const emit = defineEmits(['hover', 'pin', 'svgloaded'])

const svgObjectRef = ref(null)
const isSeatPlanHovered = ref(false)
const svgLoadAttempted = ref(false)
const svgContent = ref('')

function getSvgElements() {
  const obj = svgObjectRef.value
  if (!obj) return document.createDocumentFragment().children

  return obj.querySelectorAll('[id^="p-"]')
}

function saveOriginalColors() {
  getSvgElements().forEach(el => {
    if (!el.dataset.originalFill) {
      el.dataset.originalFill = el.getAttribute('fill')
      //el.style.cursor = 'pointer';
    }
  })
}

function updateColors(cat) {
  const svgEls = getSvgElements()
  if (!svgEls.length) return

  svgEls.forEach(el => {
    el.setAttribute('fill', el.dataset.originalFill)
  })
  if (!cat) {
    return
  }

  const planCat = props.categories.find(
    c => c.data.ticket_category_id === cat.id
  )
  const ids = planCat ? planCat.data.path_ids.map(id => `p-${id}`) : []

  svgEls.forEach(el => {
    if (!ids.includes(el.id)) {
      el.setAttribute('fill', '#E2E3E6')
    }
  })
}

const allPathIds = ref(
  [...new Set(props.categories.flatMap(cat => cat.data.path_ids))]
)

let debounceTimer: number | null = null
function setSeatPlanHoveredDebounced(value: boolean, delay = 50) {
  if (debounceTimer) clearTimeout(debounceTimer)

  debounceTimer = window.setTimeout(() => {
    isSeatPlanHovered.value = value
  }, delay)
}

function onObjectLoaded() {
  saveOriginalColors()

  getSvgElements().forEach(el => {
    el.addEventListener('mouseenter', () => {
      const id = el.id.replace('p-', '')
      emitCategoryHover(id)
      if (allPathIds.value.includes(Number(id))) {
        setSeatPlanHoveredDebounced(true)
      }
    })

    el.addEventListener('mouseleave', () => {
      emitCategoryHover(null)
      setSeatPlanHoveredDebounced(false)
    })

    el.addEventListener('click', () => {
      const id = el.id.replace('p-', '')
      emitCategoryPin(id)
    })
  })
}

let emitTimer: any

function emitCategoryHover(id) {
  clearTimeout(emitTimer)
  emitTimer = setTimeout(() => {
    emit('hover', id ? findCategoryByPathId(id) : null)
    if (id) {
      const el = svgObjectRef.value?.querySelector(`#p-${id}`)
      if (el) updateTooltipPosition(el)
    }

  }, 50)
}

function emitCategoryPin(id) {
  emit('pin', id ? findCategoryByPathId(id) : null)
}

function findCategoryByPathId(id: number) {
  return props.categories.find(cat => {
      return cat.data.path_ids.includes(Number(id))
    }
  )?.data?.ticket_category_id || null
}

const tooltipX = ref(0)
const tooltipY = ref(0)
const zoomLevels = [1, 1.25, 1.5]
const currentZoomIndex = ref(0)

const currentZoom = computed(() => zoomLevels[currentZoomIndex.value])

function zoomIn() {
  if (currentZoomIndex.value < zoomLevels.length - 1) {
    currentZoomIndex.value++
    applyZoom()
  }
}

function zoomOut() {
  if (currentZoomIndex.value > 0) {
    currentZoomIndex.value--
    applyZoom()
  }
}

function applyZoom() {
  const svg = svgObjectRef.value?.querySelector('svg')
  if (svg) {
    const scale = currentZoom.value
    svg.setAttribute('style', `transform: scale(${scale}); transform-origin: center center;`)
  }
}

function updateTooltipPosition(el) {
  const elRect = el.getBoundingClientRect()
  const containerRect = svgObjectRef.value.parentElement.getBoundingClientRect()

  tooltipX.value = elRect.left - containerRect.left - elRect.width / 2
  tooltipY.value = elRect.top - containerRect.top - elRect.height - 20
}


watch(
  () => props.activeCategory,
  (newVal, oldVal) => {
    onCategoryHoverChange(newVal)
  }
)

let hoverTimer: any

function onCategoryHoverChange(category) {
  clearTimeout(hoverTimer)
  hoverTimer = setTimeout(() => {
    updateColors(category)
  }, 50)
}

onMounted(async () => {
  if (props.media.hasSvg) {
    try {
      const res = await fetch(props.media.source)
      if (!res.ok) throw new Error('SVG load failed')
      svgContent.value = await res.text()
      await nextTick()
      onObjectLoaded()
    } catch (e) {
      console.warn(e)
    } finally {
      svgLoadAttempted.value = true
    }
  }
})

</script>

<template>
  <div class="stadium-section">
    <ClientOnly>
      <div
        v-if="media.hasSvg"
        ref="svgObjectRef"
        class="event-info__map-svg"
        v-html="svgContent"
      />
      <img
        v-if="!media.hasSvg || (!svgContent && svgLoadAttempted)"
        :src="media.source"
        :alt="$t('string.Seating-Map')"
        class="event-info__map-image"
      />
    </ClientOnly>
    <div v-if="media.hasSvg" class="event-info__map-nav-buttons">
      <button class="event-info__map-nav-button" :disabled="currentZoomIndex === zoomLevels.length - 1" @click="zoomIn">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_5680_2309)">
            <path d="M4.15909e-07 7.99444L16 7.99445" stroke="#121318" stroke-width="2"
                  stroke-miterlimit="10" />
            <path d="M7.99438 16L7.99439 4.15909e-07" stroke="#121318" stroke-width="2"
                  stroke-miterlimit="10" />
          </g>
          <defs>
            <clipPath id="clip0_5680_2309">
              <rect width="16" height="16" fill="white" transform="translate(16 16) rotate(-180)" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <button class="event-info__map-nav-button" :disabled="currentZoomIndex === 0" @click="zoomOut">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_5680_1944)">
            <path d="M0.63 7.81L15 7.81" stroke="#121318" stroke-width="2" stroke-miterlimit="10" />
          </g>
          <defs>
            <clipPath id="clip0_5680_1944">
              <rect width="14.37" height="14.37" fill="white" transform="translate(15 15) rotate(-180)" />
            </clipPath>
          </defs>
        </svg>
      </button>
    </div>

    <template v-if="isSeatPlanHovered && activeCategory">
      <div :style="{ top: `${tooltipY - 30}px`, left: `${tooltipX}px`, borderLeftColor: activeCategory.colour }"
           class="svg-tooltip">
        <h4>{{ activeCategory.title }}</h4>
      </div>
      <div class="indicator-wrapper" :style="{ top: `${tooltipY}px`, left: `${tooltipX}px` }">
        <svg width="55" height="55" viewBox="0 0 55 55" xmlns="http://www.w3.org/2000/svg">
          <circle cx="27.5" cy="27.5" r="22.5" :fill="activeCategory.colour" fill-opacity="0.3" />
          <circle cx="27.5" cy="27.5" r="27.5" :fill="activeCategory.colour" fill-opacity="0.4" />
          <circle cx="27.5" cy="27.5" r="17.5" :fill="activeCategory.colour" fill-opacity="0.4" />
          <circle cx="27.5" cy="27.5" r="9.5" fill="white" stroke="#1E1B02" stroke-width="2" />
        </svg>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import '@boats/ui/styles/config';


.svg-tooltip {
  height: 40px;
  z-index: 3;
  position: absolute;
  background: white;
  padding: 10px 14px;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  border-left: 4px solid;
  font-family: Poppins, serif;
  font-size: 14px;
  font-weight: 500;
  max-width: 260px;
  margin-bottom: -10px;
  text-align: left;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.indicator-wrapper {
  z-index: 2;
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
}


.stadium-section {
  width: 100%;
  height: 100%;
  position: relative;
}

.event-info__map-image {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  height: 100%;

  @include media-breakpoint-up(lg) {
    height: unset;
    position: relative;
    width: 100%;
    max-width: 1000px;
    padding: 2rem;
  }
}

.event-info__map-svg {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  height: 100%;
  min-width: 400px;

  @media (max-width: 399px) {
    min-width: 100svw;
  }

  @include media-breakpoint-up(lg) {
    height: unset;
    position: relative;
    width: 100%;
    max-width: 1000px;
    padding: 2rem;
  }
}

.event-info__map-nav-buttons {
  display: none;

  @include media-breakpoint-up(lg) {
    position: absolute;
    display: flex;
    flex-direction: column;
    row-gap: 6px;
    top: 33px;
    right: 36px;

    > button {
      width: 40px;
      height: 40px;
      outline: none;
      cursor: pointer;
      padding: 0;
      background-color: $white;
      border: 1px solid $black;
      border-radius: 2px;

      &:disabled {
        background: #d8d8d8;
        pointer-events: none;
      }

      &:hover {
        color: $white;
        background-color: $black;

        & > svg path {
          stroke: $white;
        }
      }

      svg {
        display: unset;
      }
    }
  }
}
</style>
